import { SearchableDecisionInstantSearch } from '@livv/models';
import { completePreviewLink, getDecisionLink } from '@livv/utils/helpers';
import { Box, Button, Grid, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import { Hit } from 'instantsearch.js';
import { FC } from 'react';
import { Highlight } from 'react-instantsearch';
import AnalysisSnippet from './AnalysisSnippet';
import PremiumFunctionalityIndicator from '@components/PremiumFunctionalityIndicator';
import AnalysisPathTitle from '@components/search/preview/DecisionAnalysed/AnalysisPathTitle';
import BlurredLorem from '@components/search/preview/DecisionAnalysed/BlurredLorem';
import { APP_URL } from '@utils/consts/livv';
import { useFreemiumDialog } from '@utils/context/freemiumDialog';
import useAmplitude from '@utils/hooks/useAmplitude';
import { HitWithSnippetResult } from '@utils/hooks/useSnippetAnalysis';
import { sxs } from '@utils/mui/sx';
import { hasAnalysisFullTextMatch } from '@utils/search/snippet';
import { TrackingEventName } from '@utils/types/amplitude/events';

interface AnalysisListProps {
    hit: SearchableDecisionInstantSearch;
    hasFullText: boolean;
    query?: string;
}

export const limitToTwoLinesSx = {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const AnalysisList: FC<AnalysisListProps> = ({ hasFullText, hit, query }) => {
    const { track } = useAmplitude();
    const { openFreemiumDialog } = useFreemiumDialog();

    const { analyses, baseObjectID: decisionId } = hit;

    const handleClick = async (analysisId: string) => {
        await track({
            name: TrackingEventName.ANALYSIS_RESULT_CLICKED,
            params: {
                analysis_id: analysisId,
                decision_id: decisionId,
            },
        });
    };

    const displayContent = (fullText: string, index: number) => {
        if (!hasFullText) {
            return openFreemiumDialog ? (
                <BlurredLorem lines={1} />
            ) : (
                <Typography
                    color="textPrimary"
                    component="span"
                    data-test-id="analysis-full-text"
                    sx={sxs([limitToTwoLinesSx, { lineHeight: 1.25 }])}
                    variant="body2"
                >
                    {fullText}
                </Typography>
            );
        }

        return openFreemiumDialog ? (
            <BlurredLorem lines={1} />
        ) : (
            <AnalysisSnippet
                hit={hit as Hit<SearchableDecisionInstantSearch> & HitWithSnippetResult}
                index={index}
            />
        );
    };

    return analyses.map((analysis, index) => {
        const { baseObjectID: analysisId, fullText } = analysis;
        const baseHref = getDecisionLink(decisionId, analysisId);
        const analysisHasFullTextMatch =
            hasFullText || hasAnalysisFullTextMatch(hit as HitWithSnippetResult, index);

        return (
            <Grid key={analysisId} sx={{ ml: 0, mt: 2 }} item xs>
                <Box mb={0.5}>
                    <Box alignItems="center" display="flex">
                        <PremiumFunctionalityIndicator sx={{ mr: 1 }} />
                        <Typography
                            data-test-id="keywordTitle"
                            sx={{
                                fontWeight: 'fontWeightMedium',
                            }}
                            variant="body2"
                        >
                            <Highlight attribute={['analyses', `${index}`, 'title']} hit={hit} />
                        </Typography>
                    </Box>

                    <AnalysisPathTitle analysis={analysis} hit={hit} />
                </Box>
                {!openFreemiumDialog ? (
                    <Link
                        color="textPrimary"
                        href={completePreviewLink(
                            baseHref,
                            APP_URL,
                            analysisHasFullTextMatch ? query : undefined,
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick(analysisId);
                        }}
                        rel="noopener noreferrer"
                        target="_blank"
                        underline="hover"
                    >
                        {displayContent(fullText, index)}
                    </Link>
                ) : (
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            openFreemiumDialog();
                        }}
                        sx={{ p: 0 }}
                    >
                        {displayContent(fullText, index)}
                    </Button>
                )}
            </Grid>
        );
    });
};

export default AnalysisList;
